<template>
  <div class="content-wrapper">
    <div class="portal-credits-shell">
      <section class="portal-left">
        <div class="hero-card">
          <div class="hero-kicker">Portal Credits</div>
          <h2>Buy credits and track usage across the portal</h2>
          <p>
            Screening and credit-based training assignments now use the same company credit pool.
            External screening still follows the fixed 45-credit rule, while training consumes the
            credits configured on each course.
          </p>
        </div>

        <div class="summary-grid" v-if="summaryLoaded">
          <div class="summary-box">
            <div class="summary-label">Available Credits</div>
            <div class="summary-value text-success">{{ summary.available_credits || 0 }}</div>
          </div>
          <div class="summary-box">
            <div class="summary-label">Total Credits</div>
            <div class="summary-value">{{ summary.total_credits || 0 }}</div>
          </div>
          <div class="summary-box">
            <div class="summary-label">Used Credits</div>
            <div class="summary-value text-warning">{{ summary.used_credits || 0 }}</div>
          </div>
        </div>

        <div class="purchase-card" v-if="apiComplete">
          <div class="purchase-grid">
            <div>
              <div class="section-heading">
                <h4>Buy More Credits</h4>
                <p>Add generic portal credits for screening orders and credit-based course assignments.</p>
              </div>

              <div class="credit-options">
                <button class="credit-option" @click="addCredits(5)">
                  <span>Quick Add</span>
                  <strong>5 Credits</strong>
                </button>
                <button class="credit-option" @click="addCredits(10)">
                  <span>Quick Add</span>
                  <strong>10 Credits</strong>
                </button>
                <button class="credit-option" @click="addCredits(25)">
                  <span>Quick Add</span>
                  <strong>25 Credits</strong>
                </button>
              </div>

              <div class="custom-credit-box">
                <label class="custom-label">Custom Credits</label>
                <div class="custom-row">
                  <app-input
                    type="number"
                    class="form-control"
                    v-model="credits.custom"
                    min="0"
                  />
                  <button class="btn btn-outline-primary" @click="addCredits(credits.custom)">
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div class="cart-section">
              <div class="cart-kicker">Credit Cart</div>
              <div class="cart-total">{{ credits.total_credits || 0 }}</div>
              <div class="cart-caption">credits selected</div>

              <div class="cart-meta">
                <div>
                  <span>Total Credits</span>
                  <strong>{{ credits.total_credits || 0 }}</strong>
                </div>
                <div>
                  <span>Total Amount</span>
                  <strong>{{ money(credits.price) }}</strong>
                </div>
              </div>

              <button class="btn btn-success w-100" @click="submit" :disabled="!credits.total_credits">
                Purchase Now
              </button>
            </div>
          </div>
        </div>

        <div class="usage-grid" v-if="apiComplete">
          <div class="usage-card">
            <div class="usage-header">
              <h5>Internal Screening</h5>
              <span class="usage-chip">{{ creditsLabel(credits.Internal_Screening, 10) }}</span>
            </div>
            <p>{{ internalScreeningDescription }}</p>
            <div class="usage-footer">Reference top-up: {{ money(credits.Internal_Screening.Price) }}</div>
          </div>

          <div class="usage-card accent-card">
            <div class="usage-header">
              <h5>External Screening</h5>
              <span class="usage-chip">{{ externalScreeningLabel }}</span>
            </div>
            <p>{{ externalScreeningDescription }}</p>
            <div class="usage-footer">Fixed usage rule: 45 credits per external screening</div>
          </div>

          <div class="usage-card usage-wide">
            <div class="usage-header">
              <h5>Training Assignments</h5>
              <span class="usage-chip">Per course credit setting</span>
            </div>
            <p>
              Training courses do not use the external screening credit amount. Each credit-based
              course consumes its own configured credit usage when the company assigns that course to an employee.
            </p>
            <div class="usage-footer">Shown on each course as Free or Credits Required</div>
          </div>
        </div>
      </section>

      <aside class="portal-right">
        <div class="transaction-card">
          <div class="section-heading">
            <h4>Recent Transactions</h4>
            <p>Latest credit purchases and credit movement for the company.</p>
          </div>

          <div v-if="transactions.length" class="transaction-scroll">
            <div
              v-for="transaction in transactions"
              :key="transaction.id"
              class="transaction-item"
            >
              <div class="transaction-top">
                <div>
                  <div class="transaction-id">Transaction #{{ transaction.id }}</div>
                  <div class="transaction-date">{{ readableDate(transaction.created_at) }}</div>
                </div>
                <span class="badge badge-pill" :class="statusClass(transaction.invoice_status)">
                  {{ transaction.invoice_status || "N/A" }}
                </span>
              </div>

              <div class="transaction-metrics">
                <div>
                  <span class="metric-label">Added</span>
                  <strong>{{ transaction.total_credits }}</strong>
                </div>
                <div>
                  <span class="metric-label">Used</span>
                  <strong>{{ transaction.used_credits }}</strong>
                </div>
                <div>
                  <span class="metric-label">Balance</span>
                  <strong>{{ transaction.available_credits }}</strong>
                </div>
              </div>

              <div class="transaction-meta">
                <span>{{ money(transaction.amount) }}</span>
                <span>{{ transaction.invoice_id ? `Invoice #${transaction.invoice_id}` : "No invoice" }}</span>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            No credit transactions found yet.
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { FormMixin } from "@/core/mixins/form/FormMixin";
import { Credits } from "@Views/Screening/utility/apiAgent";

export default {
  name: "ScreeningCredits",
  mixins: [FormMixin],
  data() {
    return {
      credits: {
        total_credits: 0,
        custom: 0,
        price: 0,
        Internal_Screening: {},
        External_Screening: {},
      },
      apiComplete: false,
      summaryLoaded: false,
      summary: {
        total_credits: 0,
        used_credits: 0,
        available_credits: 0,
      },
      transactions: [],
    };
  },
  computed: {
    internalScreeningDescription() {
      return "Internal screening continues to use portal credits based on the configured screening rules.";
    },
    externalScreeningLabel() {
      return this.creditsLabel(this.credits.External_Screening, 45);
    },
    externalScreeningDescription() {
      return "External screening requires 45 portal credits per screening.";
    },
  },
  created() {
    this.setCredits();
    this.getCreditSummary();
    this.getTransactions();
  },
  methods: {
    setCredits() {
      Credits.getCredits().then(({ data }) => {
        this.credits.Internal_Screening = data.data["Internal Screening"] || {};
        this.credits.External_Screening = data.data["External Screening"] || {};
        this.apiComplete = true;
      });
    },
    getCreditSummary() {
      Credits.getAvailableCredits().then(({ data }) => {
        this.summary = {
          total_credits: data?.data?.total_credits || 0,
          used_credits: data?.data?.used_credits || 0,
          available_credits: data?.data?.available_credits || 0,
        };
        this.summaryLoaded = true;
      }).catch(() => {
        this.summaryLoaded = true;
      });
    },
    getTransactions() {
      Credits.getCreditTransactions().then(({ data }) => {
        this.transactions = data?.data || [];
      });
    },
    addCredits(credits) {
      const amount = Number(credits || 0);

      if (amount <= 0) {
        return;
      }

      this.credits.total_credits += amount;
      this.credits.price = this.credits.total_credits;
      this.credits.custom = 0;
    },
    submit() {
      const body = {
        total_credits: this.credits.total_credits,
        price: this.credits.price,
      };

      Credits.postCredit(body).then(({ data }) => {
        this.$toastr.s(data.message);
        setTimeout(() => {
          window.location = "/buyer/personal/invoice";
        }, 2000);
      });
    },
    readableDate(date) {
      if (!date) {
        return "---";
      }

      return new Date(date).toLocaleString();
    },
    money(amount) {
      if (amount === null || amount === undefined || amount === "") {
        return "---";
      }

      return `GBP ${amount}`;
    },
    creditsLabel(config, fallback) {
      const credits = Number(config?.["Screening Credits"] || fallback || 0);
      return credits ? `${credits} Credits Required` : "Credits Required";
    },
    statusClass(status) {
      const normalized = (status || "").toLowerCase();

      if (normalized === "succeeded") return "badge-success";
      if (normalized === "pending") return "badge-warning";
      if (normalized === "failed") return "badge-danger";

      return "badge-secondary";
    },
  },
};
</script>

<style scoped>
.content-wrapper {
  max-width: 1320px;
  margin: 0 auto;
}

.portal-credits-shell {
  display: grid;
  grid-template-columns: minmax(0, 1.75fr) minmax(320px, 0.85fr);
  gap: 1.5rem;
  align-items: start;
}

.portal-left,
.portal-right {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.hero-card,
.purchase-card,
.transaction-card,
.usage-card,
.summary-box {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
}

.hero-card {
  padding: 1.75rem;
  background: linear-gradient(135deg, #f8fbff 0%, #edf8ff 55%, #f4fbf5 100%);
}

.hero-kicker,
.cart-kicker {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.hero-kicker {
  color: #1d4ed8;
  margin-bottom: 0.65rem;
}

.hero-card h2 {
  margin-bottom: 0.8rem;
  color: #0f172a;
  font-size: 2rem;
}

.hero-card p {
  margin: 0;
  color: #475569;
  max-width: 760px;
  line-height: 1.6;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.summary-box {
  padding: 1rem 1.25rem;
}

.summary-label {
  font-size: 0.85rem;
  color: #64748b;
  margin-bottom: 0.4rem;
}

.summary-value {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
}

.purchase-card,
.transaction-card {
  padding: 1.5rem;
}

.purchase-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(260px, 0.85fr);
  gap: 1.25rem;
}

.section-heading h4 {
  margin-bottom: 0.35rem;
  color: #0f172a;
}

.section-heading p {
  margin: 0;
  color: #64748b;
  line-height: 1.5;
}

.credit-options {
  margin-top: 1.25rem;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
}

.credit-option {
  border: 1px solid #cbd5e1;
  border-radius: 14px;
  padding: 1rem;
  background: #f8fafc;
  color: #0f172a;
  text-align: left;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.credit-option span {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin-bottom: 0.35rem;
}

.credit-option strong {
  font-size: 1.15rem;
}

.credit-option:hover {
  transform: translateY(-2px);
  border-color: #60a5fa;
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.12);
}

.custom-credit-box {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  background: #f8fafc;
}

.custom-label {
  display: block;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.65rem;
}

.custom-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
}

.cart-section {
  padding: 1.25rem;
  border-radius: 16px;
  background: linear-gradient(180deg, #ecfdf5 0%, #f0fdf4 100%);
  border: 1px solid #86efac;
}

.cart-kicker {
  color: #15803d;
  margin-bottom: 0.85rem;
}

.cart-total {
  font-size: 3rem;
  font-weight: 800;
  color: #166534;
  line-height: 1;
}

.cart-caption {
  color: #166534;
  margin-top: 0.25rem;
  margin-bottom: 1rem;
}

.cart-meta {
  display: grid;
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.cart-meta span {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #15803d;
  margin-bottom: 0.2rem;
}

.cart-meta strong {
  color: #0f172a;
  font-size: 1.1rem;
}

.usage-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.usage-card {
  padding: 1.25rem;
  background: #f8fafc;
}

.accent-card {
  background: linear-gradient(180deg, #eff6ff 0%, #f8fafc 100%);
}

.usage-wide {
  grid-column: 1 / -1;
}

.usage-header {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 0.85rem;
}

.usage-header h5 {
  margin: 0;
  color: #0f172a;
}

.usage-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
}

.usage-card p {
  margin: 0;
  color: #475569;
  line-height: 1.6;
}

.usage-footer {
  margin-top: 0.95rem;
  color: #0f172a;
  font-weight: 600;
}

.portal-right {
  min-width: 0;
}

.transaction-card {
  position: sticky;
  top: 1rem;
}

.transaction-scroll {
  margin-top: 1rem;
  max-height: 720px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.transaction-item {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem;
  background: #f8fafc;
}

.transaction-item + .transaction-item {
  margin-top: 0.85rem;
}

.transaction-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.9rem;
}

.transaction-id {
  font-weight: 700;
  color: #0f172a;
}

.transaction-date {
  font-size: 0.85rem;
  color: #64748b;
  margin-top: 0.2rem;
}

.transaction-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.9rem;
}

.metric-label {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.transaction-meta {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: #475569;
}

.empty-state {
  margin-top: 1rem;
  padding: 2rem 1rem;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  border-radius: 12px;
}

@media (max-width: 1199px) {
  .purchase-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 991px) {
  .portal-credits-shell,
  .summary-grid,
  .credit-options,
  .usage-grid {
    grid-template-columns: 1fr;
  }

  .transaction-card {
    position: static;
  }

  .transaction-scroll {
    max-height: 420px;
  }
}

@media (max-width: 767px) {
  .usage-header,
  .transaction-top,
  .transaction-meta {
    flex-direction: column;
  }

  .custom-row,
  .transaction-metrics {
    grid-template-columns: 1fr;
  }
}
</style>
